/**
 * Impersonation Context
 * Global state for super admin impersonation.
 *
 * Its job is not the banner — it is the IDENTITY. While a session is active
 * every request must travel with the impersonated bearer, and when the session
 * ends (by the admin, by expiry, or because the server rejected the token) the
 * admin must land back in their own session KNOWING it happened.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'

import type { EndImpersonationResponse } from '@bat/shared'
import { useQueryClient } from '@tanstack/react-query'

import { apiService } from '@/platform/api/api.service'
import { subscribeToImpersonationRejected } from '@/shared/services/impersonation-events'
import {
  clearActiveImpersonationSession,
  markImpersonationExpired,
  readActiveImpersonationSession,
  takeImpersonationExpiredNotice,
  takePendingImpersonationHandoff,
  writeActiveImpersonationSession,
  type ImpersonatedUserSummary,
} from '@/shared/services/impersonation-session'
import { storageService } from '@/shared/services/storage.service'

// ─────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────

interface ImpersonationState {
  /** Whether the user is currently impersonating another user */
  isImpersonating: boolean
  /** The user being impersonated */
  targetUser: ImpersonatedUserSummary | null
  /** The original admin's ID */
  originalAdminId: string | null
  /** When the impersonation session expires */
  expiresAt: string | null
}

interface ImpersonationContextValue extends ImpersonationState {
  /** End the impersonation session */
  endImpersonation: () => Promise<boolean>
  /** Whether the end impersonation is in progress */
  isEnding: boolean
  /**
   * The session ended on its own (expiry or server rejection) and the admin is
   * back in their own account. Must be SHOWN, never applied silently.
   */
  hasExpiredNotice: boolean
  /** Acknowledge the expiry notice. */
  dismissExpiredNotice: () => void
  /**
   * The server refused to close the session, so it is STILL OPEN there. The
   * reason it gave, or `null` when the last attempt succeeded.
   */
  endFailureReason: string | null
}

const EMPTY_STATE: ImpersonationState = {
  isImpersonating: false,
  targetUser: null,
  originalAdminId: null,
  expiresAt: null,
}

// ─────────────────────────────────────────────────────────────
// CONTEXT
// ─────────────────────────────────────────────────────────────

const ImpersonationContext = createContext<ImpersonationContextValue | undefined>(undefined)

/**
 * Point the HTTP client back at the admin's own credentials.
 *
 * `apiService` keeps the bearer in a default header, so dropping the stored
 * impersonation session is not enough on its own: the last header set would
 * keep travelling until something replaced it.
 */
const restoreOwnAuthTokenOnApiClient = (): void => {
  const ownToken = storageService.getOwnAuthToken()
  if (ownToken) {
    apiService.setAuthToken(ownToken)
  } else {
    apiService.removeAuthToken()
  }
}

// ─────────────────────────────────────────────────────────────
// PROVIDER
// ─────────────────────────────────────────────────────────────

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ImpersonationState>(EMPTY_STATE)
  const [isEnding, setIsEnding] = useState(false)
  const [hasExpiredNotice, setHasExpiredNotice] = useState(false)
  const [endFailureReason, setEndFailureReason] = useState<string | null>(null)
  const queryClient = useQueryClient()

  /** Drop the impersonated identity locally and go back to being the admin. */
  const returnToOwnSession = useCallback(() => {
    clearActiveImpersonationSession()
    restoreOwnAuthTokenOnApiClient()
    setState(EMPTY_STATE)
  }, [])

  // Adopt the session this tab is supposed to run, and INSTALL its token.
  useEffect(() => {
    // A tab that landed on `?impersonate=true` carries the hand-off written by
    // the tab that minted the session (sessionStorage is copied into a window
    // opened by `window.open`). Promote it here — and only here — so the
    // admin's own tab never starts sending the impersonated bearer.
    const urlParams = new URLSearchParams(window.location.search)
    const isImpersonationEntryPoint = urlParams.get('impersonate') === 'true'

    if (isImpersonationEntryPoint) {
      const handoff = takePendingImpersonationHandoff()
      if (handoff) {
        writeActiveImpersonationSession(handoff)
      }
      urlParams.delete('impersonate')
      const query = urlParams.toString()
      // Keep the history state: the router stores its index there (`idx`), and a blocked «back»
      // needs it to restore the URL.
      window.history.replaceState(
        window.history.state,
        '',
        query ? `${window.location.pathname}?${query}` : window.location.pathname,
      )
    }

    const session = readActiveImpersonationSession()
    if (session) {
      // The bearer is installed BEFORE any feature query runs. Everything else
      // (downloads, SSE, WebSocket) resolves it through
      // `storageService.getAuthToken()`, which now prefers this same session.
      apiService.setAuthToken(session.token)
      setState({
        isImpersonating: true,
        targetUser: session.user,
        originalAdminId: session.originalAdminId,
        expiresAt: session.expiresAt,
      })
    }

    // A notice survives the reload that dropped the impersonated identity.
    if (takeImpersonationExpiredNotice()) {
      setHasExpiredNotice(true)
    }
  }, [])

  // The server rejected the impersonated bearer (expired, or terminated by
  // another super-admin). `api.service` has already dropped it; mirror that in
  // React state and TELL the admin.
  useEffect(() => {
    return subscribeToImpersonationRejected(() => {
      restoreOwnAuthTokenOnApiClient()
      setState(EMPTY_STATE)
      setHasExpiredNotice(true)
      // Impersonated results must not survive into the admin's own screens.
      queryClient.clear()
    })
  }, [queryClient])

  // Natural expiry: the token is dead at `expiresAt` (1 h cap), so stop using
  // it at that instant rather than waiting for the next request to 401.
  useEffect(() => {
    if (!state.expiresAt) return

    const millisUntilExpiry = new Date(state.expiresAt).getTime() - Date.now()

    if (millisUntilExpiry <= 0) {
      returnToOwnSession()
      setHasExpiredNotice(true)
      return
    }

    const timeoutId = setTimeout(() => {
      returnToOwnSession()
      queryClient.clear()
      // The flag outlives the reload so the admin reads WHY the screen changed
      // instead of silently finding their own tenant's data under a banner
      // that has just disappeared.
      markImpersonationExpired()
      window.location.reload()
    }, millisUntilExpiry)

    return () => clearTimeout(timeoutId)
  }, [state.expiresAt, returnToOwnSession, queryClient])

  const endImpersonation = useCallback(async (): Promise<boolean> => {
    setIsEnding(true)
    setEndFailureReason(null)

    try {
      // Sent with the ADMIN'S OWN bearer, explicitly.
      //
      // The impersonated bearer authenticates fine, but the request then runs
      // under `frontend_user`, which has no UPDATE policy on
      // `impersonation_sessions`: the close would touch zero rows and answer
      // 200 over a session that is still open. That is the defect measured in
      // the field — the button appeared to work, and ~45 min later the panel's
      // banner rose again, still counting down. The admin's own credentials
      // carry the authority the write needs, and the server keys the session by
      // the admin's id either way.
      const ownToken = storageService.getOwnAuthToken()
      await apiService.delete<EndImpersonationResponse>(
        '/api/admin/system/impersonate',
        ownToken === null ? undefined : { Authorization: `Bearer ${ownToken}` },
      )

      // Only now, with the server's confirmation in hand.
      queryClient.clear()
      returnToOwnSession()

      // This tab exists only for the impersonated session; close it, and if the
      // browser refuses, take the admin back to the panel they came from.
      try {
        window.close()
      } catch {
        // Ignore — the redirect below is the fallback.
      }

      setTimeout(() => {
        if (!window.closed) {
          window.location.href = '/admin/system/users'
        }
      }, 100)

      return true
    } catch (error) {
      // The 401 guard in api.service retires the session when the SERVER has
      // already rejected the token — expired, or terminated by someone else.
      // Then there is nothing left open and the expiry notice is the truth.
      if (readActiveImpersonationSession() === null) {
        queryClient.clear()
        returnToOwnSession()
        setHasExpiredNotice(true)
        return true
      }

      // Otherwise the session is STILL OPEN on the server. Wiping the banner
      // here would hide a live impersonation from the only person who can
      // close it — strictly worse than showing the failure and keeping the
      // way back on screen.
      setEndFailureReason(error instanceof Error && error.message !== '' ? error.message : '')
      return false
    } finally {
      setIsEnding(false)
    }
  }, [queryClient, returnToOwnSession])

  const dismissExpiredNotice = useCallback(() => {
    setHasExpiredNotice(false)
  }, [])

  const value = useMemo(
    () => ({
      ...state,
      endImpersonation,
      isEnding,
      hasExpiredNotice,
      dismissExpiredNotice,
      endFailureReason,
    }),
    [state, endImpersonation, isEnding, hasExpiredNotice, dismissExpiredNotice, endFailureReason],
  )

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>
}

// ─────────────────────────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────────────────────────

export const useImpersonation = (): ImpersonationContextValue => {
  const context = useContext(ImpersonationContext)
  if (!context) {
    throw new Error('useImpersonation must be used within ImpersonationProvider')
  }
  return context
}
